
// Regular expressions for detecting different types of content
export const markdownHeadingPattern = /^(#+)\s+(.*)$/;
export const bulletPointPattern = /^[-•*]\s+/;
export const numberedListPattern = /^\d+\.\s+/;
export const tablePattern = /\|.*\|/;
export const swotPattern = /\b(SWOT|swot)\b|### Strengths|### Weaknesses|### Opportunities|### Threats/;
export const referencePattern = /^\d+\.\s+\[.*\]\(.+\)|References|Bibliography/i;
export const introductionPattern = /\b(Introduction|Methodology)\b/i;
export const numberedSectionPattern = /^(\d+\.)\s+(.+)$/;

// Improved SWOT section detection patterns
export const strengthsPattern = /(?:#{1,3})\s*Strengths/i;
export const weaknessesPattern = /(?:#{1,3})\s*Weaknesses/i;
export const opportunitiesPattern = /(?:#{1,3})\s*Opportunities/i;
export const threatsPattern = /(?:#{1,3})\s*Threats/i;

// Types of content sections
export type SectionType = 
  | 'heading' 
  | 'paragraph' 
  | 'bulletList' 
  | 'numberedList' 
  | 'table' 
  | 'swot' 
  | 'references' 
  | 'introduction' 
  | 'majorSection' 
  | null;

/**
 * Detects the type of section based on its content
 */
export function detectSectionType(content: string): SectionType {
  if (!content) return null;
  
  // Test the first few lines for patterns
  const lines = content.split('\n').slice(0, 5);
  const firstLine = lines[0].trim();
  
  // Check for SWOT Analysis specifically - high priority check
  if (swotPattern.test(content)) {
    return 'swot';
  }
  
  // Check if this is a heading
  if (markdownHeadingPattern.test(firstLine)) {
    return 'heading';
  }
  
  // Check if this is a reference section
  if (referencePattern.test(firstLine) || content.toLowerCase().includes('reference')) {
    return 'references';
  }
  
  // Check if this section is an introduction
  if (introductionPattern.test(firstLine)) {
    return 'introduction';
  }
  
  // Check if this is a bulleted list
  if (lines.some(line => bulletPointPattern.test(line.trim()))) {
    return 'bulletList';
  }
  
  // Check if this is a numbered list
  if (lines.some(line => numberedListPattern.test(line.trim()))) {
    return 'numberedList';
  }
  
  // Check if this section contains a table
  if (lines.some(line => tablePattern.test(line.trim()))) {
    return 'table';
  }
  
  // Default to paragraph if no other pattern matches
  return 'paragraph';
}

/**
 * Determines if the heading signifies a major section
 */
export function isMajorSection(heading: string): boolean {
  const majorSectionKeywords = [
    'executive summary',
    'overview',
    'introduction',
    'market analysis',
    'competitive landscape',
    'swot analysis',
    'trends',
    'outlook',
    'recommendations',
    'methodology',
    'conclusion'
  ];
  
  const headingLower = heading.toLowerCase();
  return majorSectionKeywords.some(keyword => headingLower.includes(keyword));
}

/**
 * Cleans a heading by removing markdown indicators and standardizing
 */
export function cleanHeading(text: string): string {
  // Remove heading markers (# symbols) and whitespace
  return text.replace(/^#+\s+/, '').trim();
}

/**
 * Extract bullet points from a list section
 */
export function extractBulletPoints(content: string): string[] {
  if (!content) return [];
  
  // Split content into lines
  const lines = content.split('\n');
  
  // Filter out lines with bullet points and clean them
  return lines
    .filter(line => bulletPointPattern.test(line.trim()))
    .map(line => line.trim().replace(bulletPointPattern, '').trim());
}

/**
 * Extract title from a list section (if present)
 */
export function extractListTitle(content: string): string | null {
  if (!content) return null;
  
  // Try to find a title before the bullet points
  const lines = content.split('\n');
  
  // Look for a non-empty line before the first bullet point
  for (let i = 0; i < lines.length; i++) {
    if (bulletPointPattern.test(lines[i].trim())) {
      // If there's a title before the first bullet point, return it
      if (i > 0 && lines[i - 1].trim().length > 0) {
        const titleLine = lines[i - 1].trim();
        // Check if this is a heading and clean it if needed
        if (markdownHeadingPattern.test(titleLine)) {
          return cleanHeading(titleLine);
        }
        return titleLine;
      }
      break;
    }
  }
  
  return null;
}

/**
 * Extract numbered list items from a list section
 */
export function extractNumberedItems(content: string): string[] {
  if (!content) return [];
  
  // Split content into lines
  const lines = content.split('\n');
  
  // Filter out lines with numbered items and clean them
  return lines
    .filter(line => numberedListPattern.test(line.trim()))
    .map(line => line.trim().replace(numberedListPattern, '').trim());
}

/**
 * Parse SWOT content to extract all components
 * Improved parsing with better section detection
 */
export function parseSwotContent(content: string): {
  mainHeading: string;
  strengths: string[];
  weaknesses: string[];
  opportunities: string[];
  threats: string[];
} {
  // Initialize result with empty arrays for bullet points
  const result = {
    mainHeading: '',
    strengths: [] as string[],
    weaknesses: [] as string[],
    opportunities: [] as string[],
    threats: [] as string[],
  };

  // Extract main SWOT heading
  const mainHeadingMatch = content.match(/^#{1,3}\s*(.*SWOT.*)/im);
  if (mainHeadingMatch) {
    result.mainHeading = mainHeadingMatch[1];
  }

  // Split content into lines for processing
  const lines = content.split('\n');
  let currentSection: 'strengths' | 'weaknesses' | 'opportunities' | 'threats' | null = null;
  
  // Process each line to extract SWOT components
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Detect section headers
    if (strengthsPattern.test(line)) {
      currentSection = 'strengths';
      continue;
    } else if (weaknessesPattern.test(line)) {
      currentSection = 'weaknesses';
      continue;
    } else if (opportunitiesPattern.test(line)) {
      currentSection = 'opportunities';
      continue;
    } else if (threatsPattern.test(line)) {
      currentSection = 'threats';
      continue;
    }
    
    // If we're in a section and the line has content (possibly a bullet point)
    if (currentSection && line) {
      // Clean bullet points if present
      const cleanedLine = line.replace(/^[-•*]\s+/, '').trim();
      
      if (cleanedLine) {
        result[currentSection].push(cleanedLine);
      }
    }
  }
  
  // Fallback: If parsing by headings didn't work, try different approach
  if (result.strengths.length === 0 && 
      result.weaknesses.length === 0 && 
      result.opportunities.length === 0 && 
      result.threats.length === 0) {
      
    // Extract sections using regex patterns
    const strengthsMatch = content.match(/#{1,3}\s*Strengths\s*\n+([\s\S]*?)(?=#{1,3}\s*(?:Weaknesses|Opportunities|Threats|$))/i);
    const weaknessesMatch = content.match(/#{1,3}\s*Weaknesses\s*\n+([\s\S]*?)(?=#{1,3}\s*(?:Opportunities|Threats|$))/i);
    const opportunitiesMatch = content.match(/#{1,3}\s*Opportunities\s*\n+([\s\S]*?)(?=#{1,3}\s*(?:Threats|$))/i);
    const threatsMatch = content.match(/#{1,3}\s*Threats\s*\n+([\s\S]*?)(?=#{1,3}\s*|$)/i);
    
    // Process extracted content into bullet points
    if (strengthsMatch && strengthsMatch[1]) {
      result.strengths = extractBulletPoints(strengthsMatch[1]) || 
                         strengthsMatch[1].split('\n').filter(line => line.trim()).map(line => line.trim());
    }
    
    if (weaknessesMatch && weaknessesMatch[1]) {
      result.weaknesses = extractBulletPoints(weaknessesMatch[1]) || 
                          weaknessesMatch[1].split('\n').filter(line => line.trim()).map(line => line.trim());
    }
    
    if (opportunitiesMatch && opportunitiesMatch[1]) {
      result.opportunities = extractBulletPoints(opportunitiesMatch[1]) || 
                             opportunitiesMatch[1].split('\n').filter(line => line.trim()).map(line => line.trim());
    }
    
    if (threatsMatch && threatsMatch[1]) {
      result.threats = extractBulletPoints(threatsMatch[1]) || 
                       threatsMatch[1].split('\n').filter(line => line.trim()).map(line => line.trim());
    }
  }
  
  return result;
}

/**
 * Check if content is part of an executive summary
 */
export function isExecutiveSummary(content: string, index: number, sections: string[]): boolean {
  if (!content || index < 0 || !sections || sections.length === 0) return false;
  
  // Check if the content itself mentions "executive summary"
  if (content.toLowerCase().includes("executive summary")) {
    return true;
  }
  
  // Look at previous sections to see if we're in an executive summary context
  for (let i = index - 1; i >= 0 && i >= index - 3; i--) {
    if (sections[i] && sections[i].toLowerCase().includes("executive summary")) {
      // This section is likely part of the executive summary
      return true;
    }
  }
  
  return false;
}
